<template>
  <Popup
    v-model:show="isVisible"
    transition="zoom"
    :click-outside-close="true"
    class="popup--transparent"
    @update:show="closePopup"
  >
    <main class="paywall-popup-container mobile-device">
      <!-- Background Decoration -->
      <div class="bg-decoration">
        <div class="blob blob-1"></div>
        <div class="blob blob-2"></div>
      </div>

      <div class="paywall-modal-card">
        <!-- Close button -->
        <span @click="closePopup" class="icon-wrapper close-btn-wrapper">
          <svg class="paywall-popup__close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30" width="14" height="14" fill="currentColor">
            <path d="M 7 4 C 6.744125 4 6.4879687 4.0974687 6.2929688 4.2929688 L 4.2929688 6.2929688 C 3.9019687 6.6839688 3.9019687 7.3170313 4.2929688 7.7070312 L 11.585938 15 L 4.2929688 22.292969 C 3.9019687 22.683969 3.9019687 23.317031 4.2929688 23.707031 L 6.2929688 25.707031 C 6.6839688 26.098031 7.3170313 26.098031 7.7070312 25.707031 L 15 18.414062 L 22.292969 25.707031 C 22.682969 26.098031 23.317031 26.098031 23.707031 25.707031 L 25.707031 23.707031 C 26.098031 23.316031 26.098031 22.682969 25.707031 22.292969 L 18.414062 15 L 25.707031 7.7070312 C 26.098031 7.3170312 26.098031 6.6829688 25.707031 6.2929688 L 23.707031 4.2929688 C 23.316031 3.9019687 22.682969 3.9019687 22.292969 4.2929688 L 15 11.585938 L 7.7070312 4.2929688 C 7.5115312 4.0974687 7.255875 4 7 4 z"/>
          </svg>
        </span>

        <!-- Step content with smooth slide transition -->
        <Transition name="fade-slide" mode="out-in">
          <!-- Step 2: Verify Email for Existing Purchase -->
          <div v-if="currentStep === 2" key="step-2" class="paywall-modal-body verification-step">
            <div class="paywall-title-group">
              <!-- <div class="paywall-limit-icon">
                <img :src="logoIconUrl" alt="Logo" class="paywall-limit-logo" />
              </div> -->
              <h1>Verify Your Purchase</h1>
              <p class="paywall-subtitle">You have used up your free previews.<br>If you have already purchased the Unlimited Preview Pass, verify your email below to restore access.</p>
            </div>

            <div class="verification-form">
              <div class="input-group">
                <label class="input-label">Email Address</label>
                <div class="input-wrapper">
                  <input
                    v-model="email"
                    type="email"
                    placeholder="your-email@example.com"
                    class="email-input"
                    @keyup.enter="checkPurchase"
                  />
                  <span class="email-input-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </span>
                </div>
                <p v-if="errorMsg" class="error-message">{{ errorMsg }}</p>
              </div>

              <div class="verification-actions">
                <sib-button
                  class="verify-button"
                  role="primary"
                  size="md"
                  width="full"
                  round="md"
                  :loading="checkLoading"
                  @click="checkPurchase"
                >
                  Verify Email
                </sib-button>

                <div class="purchase-alternative">
                  <span>Don't have a pass yet?</span>
                  <button class="toggle-step-btn" @click="goToStep(1)">
                    Buy Unlimited Previews Pass for $2.00
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 1: Buy Pass Paywall (Current Design) -->
          <div v-else-if="currentStep === 1" key="step-1" class="paywall-modal-body">
            <div class="paywall-title-group">
              <!-- <div class="paywall-limit-icon">
                <img :src="logoIconUrl" alt="Logo" class="paywall-limit-logo" />
              </div> -->
              <h1>Unlock Unlimited Previews</h1>
              <p class="paywall-subtitle">You’ve used your free previews.<br>Get unlimited previews for just $2 and keep creating until it’s perfect.</p>
            </div>

            <!-- Selling Points -->
            <div class="paywall-selling-points">
              <div class="paywall-selling-points__title">What’s included:</div>
              
              <div class="selling-point">
                <div class="selling-point__icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="12 2 2 7 12 12 22 7 12 2s"></polygon>
                    <polyline points="2 17 12 22 22 17"></polyline>
                    <polyline points="2 12 12 17 22 12"></polyline>
                  </svg>
                </div>
                <div class="selling-point__content">
                  <strong>Unlimited previews on all products</strong>
                  <span>or our artists make it right for you</span>
                </div>
              </div>
              <div class="selling-point">
                <div class="selling-point__icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                </div>
                <div class="selling-point__content">
                  <strong>$2 OFF your physical product</strong>
                  <span>if you choose to order later</span>
                </div>
              </div>
              <div class="selling-point">
                <div class="selling-point__icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                </div>
                <div class="selling-point__content">
                  <strong>HD digital copy</strong>
                  <span>of your final design</span>
                </div>
              </div>
            </div>

            <!-- Sticky CTA & Footer Links -->
            <div class="paywall-cta-wrapper">
              <sib-button
                class="paywall-cta-button"
                role="primary"
                size="md"
                width="full"
                round="md"
                :loading="loading"
                @click="handleGetPass"
              >
                Buy Unlimited Pass — $2.00
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </sib-button>

              <div class="back-to-verify-wrapper">
                <span>Already purchased?</span>
                <button class="back-to-verify-btn" @click="goToStep(2)">
                  Check existing purchase / Go Back
                </button>
              </div>
            </div>
          </div>
        </Transition>

      </div>
    </main>
  </Popup>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import Popup from '@/ui-kits/Popup.vue'
import useProductStore from '@/store/product'
import useCartStore from '@/store/cart'
import Http from '@/services/http'
import Analytics from '@/services/analytics'
import CacheService, { getJSONfromCacheResponse } from '@/services/cache'
import {
  EVENT_PRODUCT_AI_PAYWALL_POPUP_SHOWN,
  EVENT_PRODUCT_AI_PAYWALL_ADD_TO_CART,
  EVENT_PRODUCT_AI_PAYWALL_STEP_SWITCH,
  EVENT_PRODUCT_AI_EMAIL_VERIFY_SUCCESS,
  EVENT_PRODUCT_AI_EMAIL_VERIFY_FAILED,
  getShopCode,
  CACHE_KEY_PRODUCT_AI_VERIFIED_EMAIL,
  SHOP_DOMAIN,
  AI_STYLE_TRANSFER_RECENTLY_CACHE_PREFIX,
} from '@/utils'

const shopCode = getShopCode()
const backendUrl = import.meta.env.VITE_BACKEND_SERVICE_API_URL || ''
const productStore = useProductStore()
const cartStore = useCartStore()

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  productId: {
    type: [String, Number],
    default: '',
  },
  numberUploadAiImg: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['update:show', 'addToCart', 'verified', 'paywallException'])

const isVisible = ref(props.show)
const loading = ref(false)
const currentStep = ref(1)
const email = ref('')
const errorMsg = ref('')
const checkLoading = ref(false)

// Unlimited Pass Shopify Handle - đồng nhất giữa các store
const PASS_PRODUCT_HANDLE = 'unlimited-preview-pass'
const passVariantId = ref('')

async function fetchPassVariant() {
  try {
    if (passVariantId.value) return

    const product = await productStore.getProductByHandle({ 
      handle: PASS_PRODUCT_HANDLE,
      isForceLoadNewData: true
    })

    if (product?.variants?.length) {
      passVariantId.value = product.variants[0].id
    } else {
      // nếu sản phẩm không tồn tại thì cho pass luôn
      Analytics.log('ai_paywall_product_not_found', { 
        handle: PASS_PRODUCT_HANDLE,
        shopCode 
      })
      emit('paywallException')
      closePopup()
    }
  } catch (error: any) {
    console.error('Failed to fetch pass variant:', error)
    Analytics.log('paywallException', {
      handle: PASS_PRODUCT_HANDLE,
      shopCode 
    })
    emit('paywallException')
    closePopup()
  }
}

onMounted(() => {
  watch(
    () => props.show,
    (val) => {
      isVisible.value = val
      if (val) {
        currentStep.value = 1
        email.value = ''
        errorMsg.value = ''
        trackPaywallEvent(EVENT_PRODUCT_AI_PAYWALL_POPUP_SHOWN)
        fetchPassVariant()
      }
    }
  )
})

const closePopup = () => {
  isVisible.value = false
  emit('update:show', false)
}

function goToStep(step: number) {
  const fromStep = currentStep.value
  currentStep.value = step
  trackPaywallEvent(EVENT_PRODUCT_AI_PAYWALL_STEP_SWITCH, {
    from_step: fromStep,
    to_step: step
  })
}

async function trackPaywallEvent(eventName: string, extraData: Record<string, any> = {}) {
  try {
    const productHandle = productStore.product?.handle
    const cacheKey = `${AI_STYLE_TRANSFER_RECENTLY_CACHE_PREFIX}_${productHandle}`
    const cacheResponse = await CacheService.instance?.get(cacheKey)
    const historyImages = getJSONfromCacheResponse(cacheResponse) || []
    

    Analytics.track(eventName, {
      product_id: props.productId || null,
      product_handle: productHandle,
      email: email.value || null,
      current_step: currentStep.value,
      history_images: historyImages.map((i: any) => ({
        ai_image_url: i.image,
        original_upload_url: i.imageUploadUrl
      })),
      shop_code: shopCode,
      ...extraData,
    } as Record<string, any>)
  } catch (e) {
    console.error('Tracking error:', e)
  }
}

async function checkPurchase() {
  if (!email.value || !email.value.includes('@')) {
    errorMsg.value = 'Please enter a valid email address.'
    return
  }

  errorMsg.value = ''
  checkLoading.value = true

  try {
    const normalizedEmail = email.value.toLowerCase().trim()

    const response: any = await Http.post(
      `${backendUrl}/api/style_transfer/throttle-quota`,
      JSON.stringify({
        email: normalizedEmail,
        store: SHOP_DOMAIN
      }),
      {
        'Content-Type': 'application/json'
      }
    )
    
    // Check if user is not throttled (i.e. has active remaining quota)
    const hasQuota = response?.data?.is_throttled === false
    
    // Thêm thông tin lượt gen vào Analytics.log
    try {
      Analytics.log('ai_paywall_email_verify_quota', {
        email: normalizedEmail,
        is_throttled: response?.data?.is_throttled,
        remaining: response?.data?.quota?.remaining,
        total: response?.data?.quota?.total,
        shopCode,
      })
    } catch (logErr) {}

    if (hasQuota) {
      localStorage.setItem(CACHE_KEY_PRODUCT_AI_VERIFIED_EMAIL, normalizedEmail)
      trackPaywallEvent(EVENT_PRODUCT_AI_EMAIL_VERIFY_SUCCESS, { 
        verified_email: normalizedEmail,
      })
      emit('verified')
      closePopup()
      return
    } else {
      errorMsg.value = 'This email has no remaining quota or has not purchased the Unlimited Pass yet.'
      trackPaywallEvent(EVENT_PRODUCT_AI_EMAIL_VERIFY_FAILED, { 
        attempted_email: normalizedEmail,
        error_message: errorMsg.value 
      })
      return
    }

  } catch (err: any) {
    try {
      const paywallEmail = email.value
      Analytics.log('paywall_email_verify_exception', {
        message: err?.message || err,
        email: paywallEmail,
        shopCode,
      })
    } catch (logError) {}
    console.error('Failed to check purchase', err)
    errorMsg.value = err.message || 'Verification failed. Please try again.'
  } finally {
    checkLoading.value = false
  }
}

async function handleGetPass() {
  loading.value = true

  try {
    // Lấy Variant ID động từ handle đã fetch khi mở popup
    const merchandiseId = passVariantId.value
    
    if (!merchandiseId) {
      Analytics.log('ai_paywall_merchandise_id_missing', {
        handle: PASS_PRODUCT_HANDLE,
        shopCode
      })
      emit('paywallException')
      closePopup()
      return
    }

    // Thêm sản phẩm Unlimited Pass vào giỏ hàng kèm link quay lại sản phẩm chính
    const cart = await cartStore.addToCart({
      merchandiseId: merchandiseId,
      quantity: 1,
      attributes: [
        {
          key: 'Continue Your Design',
          value: window.location.origin + window.location.pathname,
        },
      ],
    })

    // Kiểm tra xem sản phẩm đã thực sự nằm trong giỏ hàng chưa
    // giỏ hàng có thể trống nếu ID variant không hợp lệ với store hiện tại
    const isAdded = cart?.lines?.some((line: any) => line.merchandise?.id === merchandiseId)

    if (!isAdded) {
      Analytics.log('ai_paywall_add_to_cart_failed_silent', {
        merchandiseId,
        cartLines: cart?.lines
      })
      emit('paywallException')
      closePopup()
      return
    }

    trackPaywallEvent(EVENT_PRODUCT_AI_PAYWALL_ADD_TO_CART)

    // chuyển đến trang thanh toán
    setTimeout(() => {
      loading.value = false
      closePopup()

      if (cartStore.cart?.checkoutUrl) {
        window.location.href = cartStore.cart?.checkoutUrl
      } else {
        emit('addToCart') // Fallback
      }
    }, 500)

  } catch (error: any) {
    try {
      Analytics.log('paywall_add_to_cart_exception', {
        message: error?.message || error,
        shopCode,
        productId: props.productId,
        numberUploadAiImg: props.numberUploadAiImg,
      })
    } catch (logError) {}
    console.error('Failed to buy unlimited pass', error)
    loading.value = false
  }
}
</script>

<style scoped lang="scss">
h1, p {
  margin: 0;
}
.paywall-popup-container {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.bg-decoration {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;

  .blob {
    position: absolute;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.15;
    mix-blend-mode: multiply;
  }

  .blob-1 {
    top: -10%;
    left: -10%;
    background: radial-gradient(circle, #10b981 0%, rgba(16, 185, 129, 0) 70%);
  }

  .blob-2 {
    bottom: -10%;
    right: -10%;
    background: radial-gradient(circle, #f59e0b 0%, rgba(245, 158, 11, 0) 70%);
  }
}

.paywall-modal-card {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 550px;
  background-color: #ffffff;
  border-radius: var(--border-radius-large);
  box-shadow: 0 12px 40px rgba(26, 27, 34, 0.08);
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

.paywall-modal-body {
  padding: 2px;
  overflow-y: auto;
  flex: 1;
}

.close-btn-wrapper {
  position: absolute;
  top: 2px;
  right: 2px;
  cursor: pointer;
  z-index: 20;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  transition: background var(--transition-duration) ease;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
}

.paywall-popup__close-icon {
  width: 14px;
  height: 14px;
  color: var(--text-color);
}

.paywall-title-group {
  text-align: center;
  margin-bottom: 10px;

  .paywall-limit-icon {
    display: none;

    .paywall-limit-logo {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  h1 {
    font-size: 32px;
    font-weight: 800;
    color: var(--text-color);
    line-height: 1.20;
    margin-top: 24px;
  }

  .paywall-subtitle {
    margin-top: 8px;
    font-size: 18px;
    color: var(--text-color-light, #666);
    line-height: 1.5;
  }
}

.paywall-selling-points {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;

  &__title {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-color);
    margin-bottom: -2px;
  }
}

.selling-point {
  display: flex;
  align-items: flex-start;
  gap: 6px;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #f8f8f9;
    color: var(--text-color);
    flex-shrink: 0;

    svg {
      color: #10b981;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 4px;

    strong {
      font-size: 18px;
      font-weight: 600;
      color: var(--text-color);
    }

    span {
      font-size: 18px;
      color: var(--text-color-light, #666);
      line-height: 1.45;
    }
  }
}

.paywall-cta-wrapper {
  position: sticky;
  bottom: -53px;
  z-index: 15;
  padding: 6px;
  padding-bottom: 0px;
  margin-left: -2px;
  margin-right: -2px;
  border-top: 1px solid var(--border-color, #eee);
  background: #ffffff;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.03);

  .paywall-cta-button {
    height: 50px;
  }
}

.paywall-cta-button {
  max-width: none;
  font-weight: 600;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
  }

  :deep(.sib-button__content) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    white-space: nowrap;
    font-size: 20px;
  }
}

/* Step 1 Verification styling */
.verification-step {
  padding: 12px;
}

.verification-form {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.input-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.input-wrapper {
  position: relative;
  width: 100%;
}

.email-input {
  width: 100%;
  height: 44px;
  padding: 10px 14px 10px 42px;
  border-radius: 8px;
  border: 1px solid var(--border-color, #cbd5e1);
  background-color: #ffffff;
  color: var(--text-color);
  font-size: 15px;
  transition: all var(--animation-duration) ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
  }
}

.email-input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  pointer-events: none;
}

.error-message {
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #ef4444;
  font-weight: 500;
}

.verification-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
}

.verify-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  transition: all 0.25s ease;
  font-weight: 600;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.3);
  }

  :deep(.sib-button__content) {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  }
}

.purchase-alternative {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: var(--text-color-light, #64748b);
}

.toggle-step-btn {
  background: none !important;
  border: none;
  color: #10b981;
  font-weight: 600;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all var(--animation-duration) ease;

  &:hover {
    text-decoration: underline;
    background-color: rgba(16, 185, 129, 0.05);
  }
}

.back-to-verify-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  font-size: 14px;
  color: var(--text-color-light, #64748b);
  text-align: center;
}

.back-to-verify-btn {
  background: none !important;
  border: none;
  color: #10b981;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all var(--animation-duration) ease;

  &:hover {
    text-decoration: underline;
    background-color: rgba(16, 185, 129, 0.05);
  }
}

@media (min-width: 375px) {
  .close-btn-wrapper {
    top: 6px;
    right: 6px;
  }

  .paywall-title-group {
    .paywall-limit-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 64px;
      height: 64px;
    }

    h1 {
      font-size: 36px;
    }

    .paywall-subtitle {
      font-size: 20px;
    }
  }

  .paywall-modal-body {
    padding: 6px;
  }

  .paywall-selling-points {
    gap: 12px;
    &__title {
      font-size: 20px;
    }
  }

  .selling-point {
    &__icon {
      width: 40px;
      height: 40px;
    }

    &__content {
      strong {
        font-size: 20px;
      }

      span {
        font-size: 18px;
      }
    }
  }

  .paywall-cta-wrapper {
    padding: 12px;
    padding-bottom: 0;
    margin-left: -6px;
    margin-right: -6px;
    bottom: 0;
  }
}

@media (min-width: 390px) {
  .close-btn-wrapper {
    top: 12px;
    right: 12px;
  }

  .paywall-modal-body {
    padding: 12px;
  }

  .paywall-selling-points {
    gap: 12px;
    .selling-point {
      gap: 12px;
    }
  }
  .paywall-cta-wrapper {
    margin-left: -12px;
    margin-right: -12px;
  }
}

@media (min-width: 412px) {
  .paywall-limit-icon {
    margin-top: -4px;
  }

  .paywall-modal-body {
    padding: 24px;
  }
  .paywall-selling-points {
    gap: 12px;
    margin-bottom: 16px;
    .selling-point {
      gap: 12px;
    }
  }
  .paywall-cta-wrapper {
    padding: 24px;
    padding-bottom: 0;
    margin-left: -24px;
    margin-right: -24px;
  }
}

@media (min-width: 768px) {
  .paywall-modal-card {
    min-width: 460px;
  }
  .paywall-modal-body {
    padding: 24px;
  }
  .paywall-title-group {
    h1 {
      font-size: 36px;
      margin-top: 4px;
    }
    .paywall-subtitle {
      margin-top: 24px;
    }
  }
  .paywall-selling-points {
    margin-bottom: 24px;
  }
  .paywall-title-group {
    margin-bottom: 24px;
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.22s cubic-bezier(0.4, 0, 0.2, 1), transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
